import React, { Component, Fragment, ReactNode } from 'react';
import FormicaLabel from '../../../custom/FormicaLabel';
import {
  ArchiosExpressionProfile,
  ExpressionBuilderView,
  ExpressionContextMenuComponent,
  ExpressionTokenComponent,
  StandardExpressionBuilderView,
  Syntax,
  TokenVariable,
  VariableToken,
} from '@trlogic/ui-pack';
import VariableType from '../../../../constant/VariableType';
import { RootState } from '../../../../redux/store';
import { VelocityVariableState } from '../../../../redux/reducers/VelocityVariableReducer';
import { changeVelocityVariable } from '../../../../redux/actions/VelocityVariableActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { StreamJsonModel } from '../../../../domain/stream/stream/StreamDomain';
import CollectionNames from '../../../../constant/CollectionNames';
import { TransactionJsonModel } from '../../../../domain/stream/transaction/TransactionDomains';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import { query, repository, subscribe } from '@trlogic/client-js';
import _ from 'lodash';
import { EnrichmentBase } from '../../../../domain/enrichment/EnrichmentDomain';
import DataType from '../../../../constant/DataType';
import ExpressionBridge from '../../../../util/ExpressionBridge';
import ServiceContainer from '../../../../config/ServiceContainer';
import { ServiceNames } from '../../../../constant/ServiceNames';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import Computation = subscribe.Computation;
import SubscriptionContext = subscribe.SubscriptionContext;
import NavigationUtil from '../../../../util/NavigationUtil';
import LanguageUtil from '../../../../util/LanguageUtil';
import { ExpressionUtil } from '../../../../util/ExpressionUtil';

class VelocityVariableWhereComponent extends Component<Props, State> {
  public state = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly expressionBridge: ExpressionBridge = ServiceContainer.getInstance().get(ServiceNames.EXPRESSION_BRIDGE);
  private readonly subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);

  private computation: Computation | undefined;

  public render(): ReactNode {
    const {
      velocityVariable: { velocityVariable },
      changeVelocityVariable,
    } = this.props;
    const { expressionView } = this.state;
    if (expressionView == undefined) {
      return null;
    }
    if (velocityVariable.where == undefined) {
      velocityVariable.where = { id: 'where', value: { type: 'TokenGroup' }, children: [] };
      changeVelocityVariable(velocityVariable);
    }
    return (
      <div className={'flex flex-col gap-y-1.5 h-full '}>
        <FormicaLabel label={LanguageUtil.get({ id: 'common.label.where', defaultMessage: 'Where' })} />
        <div className={'flex flex-col w-full h-full border-[1px] border-[#E0E4E8] overflow-scroll rounded-[4px] px-5 py-5 bg-main-gray'}>
          <div className='' id='expression_builder_where'>
            {expressionView && (
              <Fragment>
                <ExpressionTokenComponent syntax={expressionView.getModel()} view={expressionView} />
              </Fragment>
            )}
            <ExpressionContextMenuComponent context={expressionView.getContext()} close={this.onClose} />
          </div>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const enrichmentSubscriptions = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.ENRICHMENTS);
    const transactionSubscriptions = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS);
    const streamSubscription = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.STREAMS);
    this.computation = subscribe.Tracker.autorun(this.tracker, [...enrichmentSubscriptions, ...transactionSubscriptions, ...streamSubscription]);
    this.build();
  }

  public componentDidUpdate(prevProps: Readonly<Props>): void {
    if (this.props.update != prevProps.update) {
      this.refreshVariables();
    }
    if (this.props.disabled != prevProps.disabled) {
      if (this.props.disabled) {
        this.state.expressionView?.pause();
      } else {
        this.state.expressionView?.resume();
      }
    }
    if (this.props.velocityUpdated != prevProps.velocityUpdated) {
      this.state.expressionView?.terminate();
      this.setState({ expressionView: undefined }, this.build);
    }
  }

  private tracker = async () => {
    const { expressionView } = this.state;
    if (
      this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.ENRICHMENTS).some((e) => e.ready()) ||
      this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.STREAMS).some((e) => e.ready()) ||
      this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS).some((e) => e.ready())
    ) {
      const variables = await this.prepareVariables();
      expressionView?.getContext().setVariables(variables);
    }
  };

  private build = async () => {
    let expression = this.props.velocityVariable.velocityVariable.where;
    const variables: Record<string, TokenVariable[]> = await this.prepareVariables();
    await this.expressionBridge.start(expression, variables);
    const expressionView: ExpressionBuilderView = new StandardExpressionBuilderView({
      el: '#expression_builder_where',
      model: expression,
      profile: new ArchiosExpressionProfile(),
      returnType: { dataType: DataType.BOOLEAN },
      onCtrlToken: ExpressionUtil.onNavigateToken,
    });
    expressionView.getContext().setVariables(variables);
    expressionView.onChange(this.onChangeSyntax as any);
    this.setState({ expressionView }, () => {
      expressionView.initialize();
      if (this.props.disabled) expressionView.pause();
    });
  };

  private prepareVariables = async (): Promise<Record<string, TokenVariable[]>> => {
    const {
      velocityVariable: { velocityVariable, permittedTenantStores },
    } = this.props;
    if (velocityVariable.stream == undefined) return {};
    let stream: StreamJsonModel | undefined = await this.repository.find(
      QueryBuilder.select().from(CollectionNames.STREAMS).where(eq('name', velocityVariable.stream)).build(),
    );
    if (velocityVariable.tenant && velocityVariable.tenant != NavigationUtil.getTenant()) {
      stream = permittedTenantStores[velocityVariable.tenant]?.find((s) => s.name == velocityVariable.stream);
    }
    if (stream == undefined) return {};
    const event: TransactionJsonModel | undefined = await this.repository.find(
      QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('name', velocityVariable.transaction)).build(),
    );
    if (event == undefined) return {};
    const streamVariables = stream.columns.map((c) => ({
      type: VariableType.STREAM_ROW_VARIABLE as any,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.STREAM_ROW_VARIABLE as any,
        properties: c.properties,
        name: c.name,
        description: '',
        args: {
          stream: stream!.name,
        },
      },
    }));
    const eventVariables = event.variables.map((v) => ({
      type: VariableType.EVENT_VARIABLE as any,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.EVENT_VARIABLE as any,
        properties: v.properties,
        name: v.name,
        args: {
          event: event.name,
        },
      },
    }));
    const enrichmentVariables: Record<
      Exclude<VariableType, VariableType.EVENT_VARIABLE | VariableType.ACTOR_VARIABLE | VariableType.STREAM_ROW_VARIABLE>,
      TokenVariable<VariableToken>[]
    > = _.groupBy(
      ((await this.repository.findMany(query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).build())) as EnrichmentBase[])
        .filter((e) => e.type != VariableType.VELOCITY)
        .map((e) => ({
          type: e.type,
          variable: {
            type: 'Common Variable',
            variableType: e.type,
            name: e.name,
            properties: e.properties,
            args: {},
          },
        })),
      (e: any) => e.type,
    ) as Record<any, any[]>;
    return {
      [VariableType.EVENT_VARIABLE]: eventVariables,
      [VariableType.STREAM_ROW_VARIABLE]: streamVariables,
      ...enrichmentVariables,
    };
  };

  public componentWillUnmount() {
    const { expressionView } = this.state;
    expressionView?.terminate();
    this.computation?.stop();
  }

  private onChangeSyntax = (updatedExpression: Syntax): void => {
    const {
      velocityVariable: { velocityVariable },
      changeVelocityVariable,
    } = this.props;
    velocityVariable.where = updatedExpression;
    changeVelocityVariable(velocityVariable);
  };

  private onClose() {}

  private refreshVariables = async () => {
    const { expressionView } = this.state;
    if (expressionView == undefined) return;
    const expression = this.props.velocityVariable.velocityVariable.select;
    const variables: Record<string, TokenVariable[]> = await this.prepareVariables();
    await this.expressionBridge.start(expression, variables);
    expressionView.getContext().setVariables(variables);
  };
}

interface Props extends ReduxProps {
  update: number;
  velocityUpdated: number;
  disabled: boolean;
}

class State {
  expressionView: ExpressionBuilderView | undefined;
  variables: Record<string, TokenVariable[]> = {};
}

const mapStateToProps = (state: RootState) => ({
  velocityVariable: state.velocityVariable as VelocityVariableState,
});

const mapDispatchToProps = { changeVelocityVariable: changeVelocityVariable };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(VelocityVariableWhereComponent);
